import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faSpinner, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { useTranslation } from 'react-i18next';
import warehouseInventoryService from '../../services/warehouseInventoryService';
import warehouseService from '../../services/warehouseService';
import productSizesService from '../../services/productSizesServices';
import validateUtils from '../../utils/validate';
import SearchableSelect from '../common/SearchableSelect';

// Cache for warehouse and product options to avoid repeated API calls
if (!window.warehouseInventoryFormCache) {
  window.warehouseInventoryFormCache = {
    warehouses: null,
    products: null,
  };
}

const WarehouseInventoryForm = ({ 
  isOpen, 
  onClose, 
  onCancel, 
  onSubmit, 
  inventory = null, 
  inventoryId = null, 
  loading = false
}) => {
  const { t } = useTranslation();
  const strings = t('warehouseInventory.forms', { returnObjects: true });
  const fields = strings.fields;
  const REQUIRED = t('common.form.requiredIndicator');
  const cache = window.warehouseInventoryFormCache;

  // State for fetching inventory data
  const [currentInventory, setCurrentInventory] = useState(inventory);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [distributionLoading, setDistributionLoading] = useState(false);
  const [optionsLoading, setOptionsLoading] = useState(false);
  
  // Guard ref to avoid duplicate fetches for the same inventory id
  const lastFetchedIdRef = useRef(null);
  const productSearchRef = useRef(null);
  const [productSearchLoading, setProductSearchLoading] = useState(false);

  // Dropdown options
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);

  // Normalize close callback
  const closeHandler = useCallback(() => {
    if (typeof onClose === 'function') return onClose();
    if (typeof onCancel === 'function') return onCancel();
    return undefined;
  }, [onClose, onCancel]);

  const [form, setForm] = useState({
    warehouse_id: [], // Changed to array for multiple selection
    product_size_id: '',
    stock_quantity: '',
    minimum_stock: '',
    pending_order: '',
  });

  // For bulk creation: track products to be added
  const [productsList, setProductsList] = useState([]);
  // For edit mode: track per-warehouse updates for the product
  const [warehouseUpdates, setWarehouseUpdates] = useState([]);

  // Helper to get available product options excluding those already selected in other rows
  const getAvailableProductOptions = (allOptions, list, currentIndex) => {
    if (!Array.isArray(allOptions)) return [];
    const selectedIds = (list || [])
      .map((it, idx) => (idx !== currentIndex ? it?.product_size_id : null))
      .filter(Boolean)
      .map(String);
    return allOptions.filter(o => !selectedIds.includes(String(o.value)));
  };

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch dropdown options once
  const fetchDropdownOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const promises = [];
      
      // Fetch warehouses if not cached
      if (!cache.warehouses) {
        promises.push(
          warehouseService.getAllWarehouses().then(result => {
            if (result.success && Array.isArray(result.data)) {
              cache.warehouses = result.data.map(w => ({
                value: w.id,
                label: w.name || `Warehouse ${w.id}`,
              }));
            } else {
              cache.warehouses = [];
            }
          }).catch(() => {
            cache.warehouses = [];
          })
        );
      }

      // Fetch product sizes if not cached (use productSizesService)
      if (!cache.products) {
        promises.push(
          productSizesService.getAllProductSizes({}, { force: false }).then((res) => {
            const rawData = res && res.success && (res.data?.items || (Array.isArray(res.data) ? res.data : null));
            if (rawData && Array.isArray(rawData)) {
              cache.products = rawData.map((p) => ({
                value: p.product_size_id || p.id,
                label: p.alias_name || p.product_size || `Product ${p.product_size_id || p.id}`,
              }));
            } else {
              cache.products = [];
            }
          }).catch(() => {
            cache.products = [];
          })
        );
      }

      await Promise.all(promises);

      setWarehouseOptions(cache.warehouses || []);
      setProductOptions(cache.products || []);
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
    } finally {
      setOptionsLoading(false);
    }
  }, [cache]);

  const handleProductSearch = useCallback((term) => {
    if (productSearchRef.current) clearTimeout(productSearchRef.current);
    if (!term) return;
    productSearchRef.current = setTimeout(async () => {
      setProductSearchLoading(true);
      try {
        const res = await productSizesService.getAllProductSizes({ search: term, limit: 20, skip: 0 }, { force: true });
        if (res && res.success) {
          const items = res.data?.items || (Array.isArray(res.data) ? res.data : []);
          const newOptions = items.map((p) => ({
            value: p.product_size_id || p.id,
            label: p.alias_name || p.product_size || `Product ${p.product_size_id || p.id}`,
          }));
          setProductOptions(prev => {
            // Preserve currently-selected items not returned by the search
            const currentlySelected = new Set();
            productsList.forEach(r => { if (r.product_size_id) currentlySelected.add(String(r.product_size_id)); });
            const newValues = new Set(newOptions.map(o => String(o.value)));
            const preserved = prev.filter(o => currentlySelected.has(String(o.value)) && !newValues.has(String(o.value)));
            return [...preserved, ...newOptions];
          });
        }
      } finally {
        setProductSearchLoading(false);
      }
    }, 400);
  }, [productsList]);

  // Fetch inventory data when inventoryId is provided
  const fetchInventoryData = useCallback(async (id) => {
    if (!id) return null;

    // If we've already initiated a fetch for this id, skip
    if (lastFetchedIdRef.current === id) return null;

    lastFetchedIdRef.current = id;
    setInventoryLoading(true);
    try {
      const result = await warehouseInventoryService.getInventoryById(id, { force: true });
      if (result && result.success && result.data) {
        setCurrentInventory(result.data);
        // fetch product distribution for edit mode (prefill warehouse updates)
        try {
          const prodId = result.data.product_size_id || result.data.product_details?.id || null;
          if (prodId) {
            setDistributionLoading(true);
            const dist = await warehouseInventoryService.getProductDistribution(prodId, { force: true });
            if (dist && dist.success && dist.data) {
              // data may be an object containing warehouse_breakdown or an array
              const breakdown = dist.data.warehouse_breakdown || (Array.isArray(dist.data) ? dist.data : dist.data.items || []);
              const updates = Array.isArray(breakdown) ? breakdown.map((wh) => ({
                warehouse_id: wh.warehouse_id || wh.id || wh.warehouseId,
                warehouse_name: wh.warehouse_name || wh.name || wh.warehouse_name,
                stock_quantity: wh.stock_quantity != null ? String(wh.stock_quantity) : String(wh.available_inventory ?? ''),
                minimum_stock: wh.minimum_stock != null ? String(wh.minimum_stock) : String(wh.minimum_stock ?? ''),
                pending_order: wh.pending_order != null ? String(wh.pending_order) : String(wh.pending_order ?? '0'),
              })) : [];
              setWarehouseUpdates(updates);
            }
          }
        } catch (e) {
          console.error('Error fetching product distribution', e);
        } finally {
          setDistributionLoading(false);
        }
        return result.data;
      }
      console.error('Failed to fetch inventory:', result && result.error);
      return null;
    } catch (error) {
      console.error('Error fetching inventory:', error);
      return null;
    } finally {
      setInventoryLoading(false);
    }
  }, []);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      lastFetchedIdRef.current = null;
      setCurrentInventory(inventory || null);
    }
  }, [isOpen, inventory]);

  // Initialize form and fetch data
  useEffect(() => {
    if (!isOpen) return;

    const activeInventory = currentInventory || inventory;
    // Consider edit mode if we have an inventory object or an inventoryId
    const isEditMode = Boolean(activeInventory || inventoryId);

    // For create mode only: fetch dropdown options if not already cached
    if (!isEditMode) {
      if (!cache.warehouses || !cache.products) {
        fetchDropdownOptions();
      } else {
        setWarehouseOptions(cache.warehouses);
        setProductOptions(cache.products);
      }
    } else {
      // Edit mode: do not call dropdown APIs; clear options to avoid accidental fetches
      setWarehouseOptions([]);
      setProductOptions([]);
    }

    // If we have an inventoryId but no inventory data, fetch it (allowed in edit mode)
    if (inventoryId && !inventory && !currentInventory) {
      fetchInventoryData(inventoryId).then((fetchedInventory) => {
        if (fetchedInventory) {
          setForm({
            warehouse_id: fetchedInventory.warehouse_id ? [String(fetchedInventory.warehouse_id)] : [],
            product_size_id: String(fetchedInventory.product_size_id || ''),
            stock_quantity: String(fetchedInventory.stock_quantity || ''),
            minimum_stock: String(fetchedInventory.minimum_stock || ''),
            pending_order: String(fetchedInventory.pending_order || ''),
          });
        }
      });
      return;
    }

    // Initialize form with inventory data or defaults
    setForm({
      warehouse_id: activeInventory?.warehouse_id ? [String(activeInventory.warehouse_id)] : [],
      product_size_id: String(activeInventory?.product_size_id || ''),
      stock_quantity: String(activeInventory?.stock_quantity || ''),
      minimum_stock: String(activeInventory?.minimum_stock || ''),
      pending_order: String(activeInventory?.pending_order || ''),
    });
    setErrors({});
  }, [isOpen, inventory, currentInventory, inventoryId, fetchInventoryData, fetchDropdownOptions, cache]);

  // When options (warehouse/product) become available, ensure the form contains the
  // correct ids even if the provided inventory only includes names in details.
  useEffect(() => {
    if (!isOpen) return;

    const activeInventory = currentInventory || inventory;
    const isEditMode = Boolean(activeInventory || inventoryId);
    // Only attempt to resolve ids from options in create mode (when options are fetched)
    if (isEditMode) return;
    if (!activeInventory) return;

    // Resolve warehouse id: prefer explicit id fields, otherwise try to match by name
    let resolvedWarehouseId = activeInventory.warehouse_id || activeInventory.warehouse_id === 0 ? activeInventory.warehouse_id : null;
    if (!resolvedWarehouseId) {
      resolvedWarehouseId = activeInventory.warehouse_details?.id || activeInventory.warehouse_details?.warehouse_id || null;
    }
    if (!resolvedWarehouseId && warehouseOptions && warehouseOptions.length) {
      const nameToMatch = activeInventory.warehouse_details?.name || activeInventory.warehouse_name || '';
      if (nameToMatch) {
        const found = warehouseOptions.find(o => String(o.label) === String(nameToMatch));
        if (found) resolvedWarehouseId = found.value;
      }
    }

    // Resolve product id similarly
    let resolvedProductId = activeInventory.product_size_id || activeInventory.product_size_id === 0 ? activeInventory.product_size_id : null;
    if (!resolvedProductId) {
      resolvedProductId = activeInventory.product_details?.id || activeInventory.product_details?.product_size_id || null;
    }
    if (!resolvedProductId && productOptions && productOptions.length) {
      const prodName = activeInventory.product_details?.alias_name || activeInventory.product_name || activeInventory.product_details?.product_size || '';
      if (prodName) {
        const foundP = productOptions.find(o => String(o.label) === String(prodName));
        if (foundP) resolvedProductId = foundP.value;
      }
    }

    // Build the new form state using resolved ids (stringified) or fallbacks
    setForm(prev => ({
      warehouse_id: resolvedWarehouseId ? [String(resolvedWarehouseId)] : (prev.warehouse_id || []),
      product_size_id: String(resolvedProductId ?? prev.product_size_id ?? ''),
      stock_quantity: String(activeInventory.stock_quantity ?? prev.stock_quantity ?? ''),
      minimum_stock: String(activeInventory.minimum_stock ?? prev.minimum_stock ?? ''),
      pending_order: String(activeInventory.pending_order ?? prev.pending_order ?? ''),
    }));
    // Also initialize warehouseUpdates when we have distribution data embedded in activeInventory
    if (activeInventory && activeInventory.warehouse_breakdown && Array.isArray(activeInventory.warehouse_breakdown)) {
      const updates = activeInventory.warehouse_breakdown.map((wh) => ({
        warehouse_id: wh.warehouse_id || wh.id,
        warehouse_name: wh.warehouse_name || wh.name,
        stock_quantity: wh.stock_quantity != null ? String(wh.stock_quantity) : String(wh.available_inventory ?? ''),
        minimum_stock: wh.minimum_stock != null ? String(wh.minimum_stock) : String(wh.minimum_stock ?? ''),
        pending_order: wh.pending_order != null ? String(wh.pending_order) : String(wh.pending_order ?? '0'),
      }));
      setWarehouseUpdates(updates);
    }
  }, [isOpen, warehouseOptions, productOptions, currentInventory, inventory, inventoryId]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    let v = value;
    
    // Apply input filters for numeric fields
    if (['stock_quantity', 'minimum_stock', 'pending_order'].includes(name)) {
      v = validateUtils.inputFilters.numeric(value);
    }
    
    setForm(prev => ({ ...prev, [name]: v }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  // Handle product list changes for bulk create mode
  const handleProductChange = (idx, field, value) => {
    setProductsList(prev => {
      const updated = [...prev];
      if (field === 'product_size_id') {
        updated[idx] = { ...updated[idx], [field]: value };
      } else if (['stock_quantity', 'minimum_stock', 'pending_order'].includes(field)) {
        updated[idx] = { ...updated[idx], [field]: validateUtils.inputFilters.numeric(value) };
      } else {
        updated[idx] = { ...updated[idx], [field]: value };
      }
      return updated;
    });
    if (errors[`product.${idx}.${field}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`product.${idx}.${field}`];
        return newErrors;
      });
    }
  };

  const addProduct = () => {
    // Validate the last product row before allowing a new empty row
    const list = Array.isArray(productsList) ? [...productsList] : [];
    const lastIndex = list.length - 1;
    if (lastIndex >= 0) {
      const last = list[lastIndex] || {};
      const newErrors = {};

      if (!last.product_size_id) newErrors[`product.${lastIndex}.product_size_id`] = fields.product_size_id.errors.required;
      if (!last.stock_quantity || isNaN(Number(last.stock_quantity)) || Number(last.stock_quantity) < 0) newErrors[`product.${lastIndex}.stock_quantity`] = fields.stock_quantity.errors.required;
      if (!last.minimum_stock || isNaN(Number(last.minimum_stock)) || Number(last.minimum_stock) < 0) newErrors[`product.${lastIndex}.minimum_stock`] = fields.minimum_stock.errors.required;
      if (last.pending_order && (isNaN(Number(last.pending_order)) || Number(last.pending_order) < 0)) newErrors[`product.${lastIndex}.pending_order`] = fields.pending_order.errors.invalid;

      if (Object.keys(newErrors).length > 0) {
        setErrors(prev => ({ ...prev, ...newErrors }));
        return; // don't add new product until previous is valid
      }
    }

    // Clear any productsList-level error and append a new empty product
    setErrors(prev => {
      const next = { ...prev };
      delete next.productsList;
      return next;
    });

    setProductsList(prev => [...prev, {
      product_size_id: '',
      stock_quantity: '',
      minimum_stock: '',
      pending_order: '',
    }]);
  };

  const removeProduct = (idx) => {
    setProductsList(prev => prev.filter((_, i) => i !== idx));
    // Remove any errors tied to this product index and shift others are left as-is
    setErrors(prev => {
      const next = {};
      Object.keys(prev || {}).forEach(k => {
        if (!k.startsWith(`product.${idx}`)) next[k] = prev[k];
      });
      // If productsList error exists and there are still products, clear it
      if (next.productsList && (productsList.length - 1) > 0) delete next.productsList;
      return next;
    });
  };

  const validate = () => {
    const v = {};
    const activeInventory = currentInventory || inventory;

    // For edit mode: use existing validation
    if (activeInventory || inventoryId) {
      // Warehouse is required (allow existing inventory to satisfy requirement)
      if (!form.warehouse_id && !(activeInventory && (activeInventory.warehouse_id || activeInventory.warehouse_details?.id))) {
        v.warehouse_id = fields.warehouse_id.errors.required;
      }
      
      // Product is required
      if (!form.product_size_id && !(activeInventory && (activeInventory.product_size_id || activeInventory.product_details?.id))) {
        v.product_size_id = fields.product_size_id.errors.required;
      }
      
      // Stock quantity is required and must be positive
      if (!form.stock_quantity || !String(form.stock_quantity).trim()) {
        v.stock_quantity = fields.stock_quantity.errors.required;
      } else if (isNaN(Number(form.stock_quantity)) || Number(form.stock_quantity) < 0) {
        v.stock_quantity = fields.stock_quantity.errors.invalid;
      }
      
      // Minimum stock is required and must be positive
      if (!form.minimum_stock || !String(form.minimum_stock).trim()) {
        v.minimum_stock = fields.minimum_stock.errors.required;
      } else if (isNaN(Number(form.minimum_stock)) || Number(form.minimum_stock) < 0) {
        v.minimum_stock = fields.minimum_stock.errors.invalid;
      }
      
      // Pending order is optional but must be valid if provided
      if (form.pending_order && String(form.pending_order).trim()) {
        if (isNaN(Number(form.pending_order)) || Number(form.pending_order) < 0) {
          v.pending_order = fields.pending_order.errors.invalid;
        }
      }
      // If we have warehouseUpdates (product distribution edit), validate them too
      if (warehouseUpdates && warehouseUpdates.length) {
        warehouseUpdates.forEach((wh, idx) => {
          if (wh.stock_quantity == null || String(wh.stock_quantity).trim() === '') {
            v[`warehouse.${idx}.stock_quantity`] = 'Stock quantity is required';
          } else if (isNaN(Number(wh.stock_quantity)) || Number(wh.stock_quantity) < 0) {
            v[`warehouse.${idx}.stock_quantity`] = 'Enter a valid stock quantity';
          }
          if (wh.minimum_stock == null || String(wh.minimum_stock).trim() === '') {
            v[`warehouse.${idx}.minimum_stock`] = 'Minimum stock is required';
          } else if (isNaN(Number(wh.minimum_stock)) || Number(wh.minimum_stock) < 0) {
            v[`warehouse.${idx}.minimum_stock`] = 'Enter a valid minimum stock';
          }
        });
      }
    } else {
      // For create mode: validate bulk products list
      if (!form.warehouse_id || (Array.isArray(form.warehouse_id) && form.warehouse_id.length === 0)) {
        v.warehouse_id = fields.warehouse_id.errors.required;
      }
      
      if (productsList.length === 0) {
        v.productsList = 'At least one product must be added';
      } else {
        // Validate each product in the list
        productsList.forEach((product, idx) => {
          if (!product.product_size_id) {
            v[`product.${idx}.product_size_id`] = fields.product_size_id.errors.required;
          }
          if (!product.stock_quantity || isNaN(Number(product.stock_quantity)) || Number(product.stock_quantity) < 0) {
            v[`product.${idx}.stock_quantity`] = fields.stock_quantity.errors.required;
          }
          if (!product.minimum_stock || isNaN(Number(product.minimum_stock)) || Number(product.minimum_stock) < 0) {
            v[`product.${idx}.minimum_stock`] = fields.minimum_stock.errors.required;
          }
          if (product.pending_order && isNaN(Number(product.pending_order))) {
            v[`product.${idx}.pending_order`] = fields.pending_order.errors.invalid;
          }
        });
      }
    }
    
    setErrors(v);
    return Object.keys(v).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      const activeInventory = currentInventory || inventory;
      const isEdit = Boolean(activeInventory || inventoryId);

      if (isEdit) {
        // Edit mode: single product update
        const resolvedWarehouseId = form.warehouse_id ? parseInt(form.warehouse_id, 10) : (activeInventory?.warehouse_id ?? activeInventory?.warehouse_details?.id ?? null);
        const resolvedProductId = form.product_size_id ? parseInt(form.product_size_id, 10) : (activeInventory?.product_size_id ?? activeInventory?.product_details?.id ?? null);

        // If we have warehouseUpdates (product distribution edit), submit bulk update payload
        if (warehouseUpdates && warehouseUpdates.length) {
          const payload = {
            product_size_id: resolvedProductId !== null ? parseInt(resolvedProductId, 10) : null,
            warehouse_updates: warehouseUpdates.map((wh) => ({
              warehouse_id: parseInt(wh.warehouse_id, 10),
              stock_quantity: wh.stock_quantity === '' ? 0 : parseFloat(wh.stock_quantity),
              minimum_stock: wh.minimum_stock === '' ? 0 : parseFloat(wh.minimum_stock),
            })),
          };
          await onSubmit(payload);
        } else {
          // Fallback to single-record update (existing behaviour)
          const payload = {
            warehouse_id: resolvedWarehouseId !== null ? parseInt(resolvedWarehouseId, 10) : null,
            product_size_id: resolvedProductId !== null ? parseInt(resolvedProductId, 10) : null,
            stock_quantity: parseFloat(form.stock_quantity),
            minimum_stock: parseFloat(form.minimum_stock),
            pending_order: form.pending_order ? parseFloat(form.pending_order) : 0,
          };
          await onSubmit(payload);
        }
      } else {
        // Create mode: bulk products for multiple warehouses
        const payload = {
          warehouse_ids: Array.isArray(form.warehouse_id) ? form.warehouse_id.map(id => parseInt(id, 10)) : [parseInt(form.warehouse_id, 10)],
          products: productsList.map(product => ({
            product_size_id: parseInt(product.product_size_id, 10),
            stock_quantity: parseFloat(product.stock_quantity),
            minimum_stock: parseFloat(product.minimum_stock),
            pending_order: product.pending_order ? parseFloat(product.pending_order) : 0,
          })),
        };

        await onSubmit(payload);
      }
      closeHandler();
    } catch (submitError) {
      console.error('Error submitting inventory form:', submitError);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const scrollClass = 'warehouse-inventory-form-scroll';
  const activeInventory = currentInventory || inventory;
  const isEdit = Boolean(activeInventory || inventoryId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
      <style>{`
        .${scrollClass} { -ms-overflow-style: none; scrollbar-width: none; }
        .${scrollClass}::-webkit-scrollbar { display: none; }
      `}</style>
      <div className={`w-full max-w-2xl relative bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto ${scrollClass}`} style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
          <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {isEdit ? strings.title.edit : strings.title.add}
          </h2>
          <button 
            onClick={closeHandler} 
            className="p-2 rounded-lg hover:opacity-75 transition-opacity" 
            style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {submitting && (
            <div className="absolute inset-0 z-40 flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)' }}>
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {isEdit ? strings.buttons.submit.loading.edit : strings.buttons.submit.loading.add}
                </p>
              </div>
            </div>
          )}
          
          {(inventoryLoading || optionsLoading) ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <FontAwesomeIcon icon={faSpinner} spin className="text-4xl mb-4" style={{ color: UI_COLORS.BUTTONS.PRIMARY.background }} />
                <p className="text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                  {inventoryLoading ? strings.loading.inventory : strings.loading.warehouses}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Warehouse display / dropdown */}
              <div>
                <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                  {fields.warehouse_id.label} {REQUIRED}
                </label>
                {isEdit ? (
                  <div className="w-full px-4 py-3 rounded-xl border-2" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, color: UI_COLORS.FORMS.INPUT.text }}>
                    {activeInventory?.warehouse_details?.name || activeInventory?.warehouse_name || fields.warehouse_id.placeholder}
                  </div>
                ) : (
                  <SearchableSelect
                    name="warehouse_id"
                    value={form.warehouse_id}
                    onChange={handleChange}
                    disabled={optionsLoading}
                    isMulti={true}
                    placeholder={fields.warehouse_id.placeholder}
                    error={errors.warehouse_id}
                    options={warehouseOptions}
                  />
                )}
                {errors.warehouse_id && (
                  <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.warehouse_id}</div>
                )}
              </div>

              {/* Product display / dropdown OR Bulk products editor */}
              {isEdit ? (
                <div>
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {fields.product_size_id.label} {REQUIRED}
                  </label>
                  <div className="w-full px-4 py-3 rounded-xl border-2 mb-4" style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, color: UI_COLORS.FORMS.INPUT.text }}>
                    <div className="font-medium">{activeInventory?.product_details?.alias_name || activeInventory?.product_name || activeInventory?.product_details?.product_size || fields.product_size_id.placeholder}</div>
                    <div className="text-sm text-gray-500">{activeInventory?.product_details?.product_size || activeInventory?.product_size}</div>
                  </div>
                  {errors.product_size_id && (
                    <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.product_size_id}</div>
                  )}

                  {/* Show product summary totals if available */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div>
                      <p className="text-xs text-gray-500">{t('warehouseInventory.table.columns.total_stock_all_warehouses')}</p>
                      <p className="font-semibold">{activeInventory?.total_stock_all_warehouses ?? t('common.symbols.emDash')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('warehouseInventory.table.columns.total_pending_all_warehouses')}</p>
                      <p className="font-semibold">{activeInventory?.total_pending_all_warehouses ?? t('common.symbols.emDash')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('warehouseInventory.table.columns.total_available_all_warehouses')}</p>
                      <p className="font-semibold">{activeInventory?.total_available_all_warehouses ?? t('common.symbols.emDash')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">{t('warehouseInventory.table.columns.warehouse_count')}</p>
                      <p className="font-semibold">{activeInventory?.warehouse_count ?? t('common.symbols.emDash')}</p>
                    </div>
                  </div>

                  {/* Distribution editor */}
                  <div className="rounded-lg border p-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <h4 className="font-semibold mb-3">Product Distribution</h4>
                    {(distributionLoading) ? (
                      <div className="py-6 text-center">Loading distribution...</div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Warehouse</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Stock Quantity</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Pending Order</th>
                              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Minimum Stock</th>
                              <th className="px-4 py-2 text-center text-xs font-medium text-gray-500">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {warehouseUpdates && warehouseUpdates.length ? warehouseUpdates.map((wh, idx) => (
                              <tr key={`${wh.warehouse_id}-${idx}`}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{wh.warehouse_name || `Warehouse ${wh.warehouse_id}`}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 text-right">
                                  <input
                                    value={wh.stock_quantity}
                                    onChange={(e) => setWarehouseUpdates(prev => {
                                      const copy = [...prev]; copy[idx] = { ...copy[idx], stock_quantity: validateUtils.inputFilters.numeric(e.target.value) }; return copy;
                                    })}
                                    className="w-28 text-right px-2 py-1 rounded border-2"
                                  />
                                  {errors[`warehouse.${idx}.stock_quantity`] && (
                                    <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`warehouse.${idx}.stock_quantity`]}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-700 text-right">{wh.pending_order ?? '0'}</td>
                                <td className="px-4 py-3 text-sm text-gray-700 text-right">
                                  <input
                                    value={wh.minimum_stock}
                                    onChange={(e) => setWarehouseUpdates(prev => {
                                      const copy = [...prev]; copy[idx] = { ...copy[idx], minimum_stock: validateUtils.inputFilters.numeric(e.target.value) }; return copy;
                                    })}
                                    className="w-20 text-right px-2 py-1 rounded border-2"
                                  />
                                  {errors[`warehouse.${idx}.minimum_stock`] && (
                                    <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`warehouse.${idx}.minimum_stock`]}</div>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <button type="button" onClick={() => setWarehouseUpdates(prev => prev.filter((_, i) => i !== idx))} className="text-red-600 px-2 py-1 rounded hover:bg-red-50">Remove</button>
                                </td>
                              </tr>
                            )) : (
                              <tr>
                                <td colSpan="5" className="text-center py-6 text-gray-500">No warehouses found for this product.</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>Products</h3>
                    {errors.productsList && (
                      <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.productsList}</div>
                    )}
                  </div>
                  
                  {productsList.map((product, idx) => (
                    <div key={idx} className="rounded-xl border p-4" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-xs font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            {fields.product_size_id.label} {REQUIRED}
                          </label>
                          <SearchableSelect
                            name="product_size_id"
                            value={product.product_size_id}
                            onChange={(e) => handleProductChange(idx, 'product_size_id', e.target.value)}
                            disabled={optionsLoading}
                            loading={productSearchLoading}
                            placeholder={fields.product_size_id.placeholder}
                            error={errors[`product.${idx}.product_size_id`]}
                            onSearchChange={handleProductSearch}
                            options={getAvailableProductOptions(productOptions, productsList, idx)}
                          />
                          {errors[`product.${idx}.product_size_id`] && (
                            <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`product.${idx}.product_size_id`]}</div>
                          )}
                        </div>
                        
                        <div>
                          <label className="block text-xs font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            Stock {REQUIRED}
                          </label>
                          <input 
                            value={product.stock_quantity}
                            onChange={(e) => handleProductChange(idx, 'stock_quantity', e.target.value)}
                            className="w-full px-3 py-2 rounded border-2" 
                            placeholder="0"
                            style={{ 
                              backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                              borderColor: errors[`product.${idx}.stock_quantity`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border 
                            }} 
                          />
                        </div>

                        <div>
                          <label className="block text-xs font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                            Min {REQUIRED}
                          </label>
                          <input
                            value={product.minimum_stock}
                            onChange={(e) => handleProductChange(idx, 'minimum_stock', e.target.value)}
                            className="w-full px-3 py-2 rounded border-2"
                            placeholder="0"
                            style={{
                              backgroundColor: UI_COLORS.FORMS.INPUT.background,
                              borderColor: errors[`product.${idx}.minimum_stock`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                            }}
                          />
                          {errors[`product.${idx}.minimum_stock`] && (
                            <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`product.${idx}.minimum_stock`]}</div>
                          )}
                        </div>

                        <div className="flex flex-col items-end justify-between">
                          <div className="flex-1">
                            <label className="block text-xs font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                              Pending
                            </label>
                            <input
                              value={product.pending_order}
                              onChange={(e) => handleProductChange(idx, 'pending_order', e.target.value)}
                              className="w-full px-3 py-2 rounded border-2"
                              placeholder="0"
                              style={{
                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                borderColor: errors[`product.${idx}.pending_order`] ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border,
                              }}
                            />
                            {errors[`product.${idx}.pending_order`] && (
                              <div className="text-xs mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors[`product.${idx}.pending_order`]}</div>
                            )}
                          </div>
                          <button 
                            type="button"
                            onClick={() => removeProduct(idx)}
                            className="p-2 rounded text-red-600 hover:bg-red-50 mt-2"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button 
                    type="button"
                    onClick={addProduct}
                    className="px-4 py-2 rounded-xl" 
                    style={{ 
                      backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, 
                      color: UI_COLORS.BUTTONS.SECONDARY.text 
                    }}
                  >
                    <FontAwesomeIcon icon={faPlus} className="mr-2" />
                    Add Product
                  </button>
                </div>
              )}

              {/* Numeric fields in a grid - only for edit mode */}
              {isEdit && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                      {fields.stock_quantity.label} {REQUIRED}
                    </label>
                    <input 
                      name="stock_quantity" 
                      value={form.stock_quantity} 
                      onChange={handleChange} 
                      className="w-full px-4 py-3 rounded-xl border-2" 
                      placeholder={fields.stock_quantity.placeholder} 
                      style={{ 
                        backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                        borderColor: errors.stock_quantity ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border 
                      }} 
                    />
                    {errors.stock_quantity && (
                      <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.stock_quantity}</div>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                      {fields.minimum_stock.label} {REQUIRED}
                    </label>
                    <input 
                      name="minimum_stock" 
                      value={form.minimum_stock} 
                      onChange={handleChange} 
                      className="w-full px-4 py-3 rounded-xl border-2" 
                      placeholder={fields.minimum_stock.placeholder} 
                      style={{ 
                        backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                        borderColor: errors.minimum_stock ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border 
                      }} 
                    />
                    {errors.minimum_stock && (
                      <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.minimum_stock}</div>
                    )}
                  </div>
                </div>
              )}

              {/* Pending order - only for edit mode */}
              {isEdit && (
                <div>
                  <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                    {fields.pending_order.label}
                  </label>
                  <input 
                    name="pending_order" 
                    value={form.pending_order} 
                    onChange={handleChange} 
                    className="w-full px-4 py-3 rounded-xl border-2" 
                    placeholder={fields.pending_order.placeholder} 
                    style={{ 
                      backgroundColor: UI_COLORS.FORMS.INPUT.background, 
                      borderColor: errors.pending_order ? UI_COLORS.STATUS.ERROR : UI_COLORS.FORMS.INPUT.border 
                    }} 
                  />
                  {errors.pending_order && (
                    <div className="text-sm mt-1" style={{ color: UI_COLORS.STATUS.ERROR }}>{errors.pending_order}</div>
                  )}
                </div>
              )}

              <div className="flex justify-end gap-4 pt-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                <button 
                  type="button"
                  onClick={closeHandler}
                  className="px-6 py-3 rounded-xl font-semibold" 
                  style={{ 
                    backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, 
                    color: UI_COLORS.BUTTONS.SECONDARY.text 
                  }}
                >
                  {strings.buttons.cancel}
                </button>
                <button 
                  type="submit" 
                  disabled={loading || submitting} 
                  className="px-6 py-3 rounded-xl font-semibold" 
                  style={{ 
                    backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, 
                    color: UI_COLORS.BUTTONS.PRIMARY.text 
                  }}
                >
                  <FontAwesomeIcon icon={faSave} className="mr-2" /> 
                  {isEdit ? strings.buttons.submit.edit : strings.buttons.submit.add}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default WarehouseInventoryForm;